/**
 * Shared Socket.IO client options for every connection the TMA opens to the
 * qgammon-gameserver (the live match socket, the ranked-queue listener and the
 * invite listener).
 *
 * WHY THIS EXISTS: the gameserver rate-limits the handshake and rejects the
 * excess with `too_many_connections` (per user: 10 burst then 30/min; per IP
 * pre-auth: 30 burst then 60/min — deliberately generous because mobile CGNAT
 * puts many legitimate players behind one address).
 *
 * 🔴 CORRECTED 2026-09-28 (board-ui 0.8 round, reperto 3 of the 27/09 TMA-orc dossier): this
 * comment used to say a rejected handshake is a `connect_error` that Socket.IO answers by
 * RECONNECTING. **False**, read in the installed socket.io-client 4.8.3 source
 * (`build/esm/socket.js`): on a `CONNECT_ERROR` packet — i.e. the middleware's own rejection,
 * `next(new Error(...))` on the gameserver — the client calls `this.destroy()` (which clears its
 * subscriptions, `active = false`) and only THEN emits `connect_error`. A rejected handshake is
 * never retried, by any `reconnection*` setting: the fault this file describes is the opposite of
 * what it used to say — not a self-sustaining reconnect loop, but a socket that goes silent and
 * stays silent. Only a TRANSPORT `connect_error` (`onerror`, subscriptions still alive — a dropped
 * WebSocket, not a refused one) goes through the Manager's reconnection, and that is what
 * `reconnectionDelay`/`reconnectionDelayMax` below actually govern.
 *
 * So these numbers exist for genuine transport blips, not for the handshake budget above: 1s
 * first retry (a real network hiccup still recovers fast) backing off to 20s, with the full ±50%
 * jitter so several reloading clients don't resynchronise into a thundering herd. Reconnection
 * stays enabled and unlimited — a Telegram WebView that suspends and resumes MUST come back on
 * its own. A REJECTED handshake (`too_many_connections` included) is a separate mechanism: from
 * board-ui ≥0.7.0 the hook itself retries `too_many_connections` with its own backoff
 * (`handshakeRetryDelayMs`, 0.5s→30s, independent of the options below); every other refusal
 * (`unauthorized`, `invalid_ticket`, an unknown code, `auth_failed`) is NOT retried by anyone —
 * see `lib/qgammon/connessione.ts` for how the TMA surfaces that to the player.
 *
 * These same values also gate `useRankedQueue.ts` and `useInvite.ts` — neither listens for
 * `connect_error`, so a refused handshake leaves them silent too, same as the table used to be:
 * out of scope for this round, declared here so it is not read as covered.
 */
export const QGAMMON_SOCKET_OPTIONS = {
  // Mutable array on purpose — socket.io-client's ManagerOptions types
  // `transports` as `string[]`, so `as const` would not assign.
  transports: ['websocket', 'polling'],
  reconnection: true,
  reconnectionDelay: 1_000,
  reconnectionDelayMax: 20_000,
  randomizationFactor: 0.5,
};
